import { EnhancedTableHead, Order } from '@components/Table/EnhancedTableHead';
import { NomineeAgent } from '@features/api/response/NomineeAgent';
import {
  Box,
  Pagination,
  SwipeableDrawer,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { headerCells } from './Agents.consts';
import { getComparator } from '@utils/table/tableUtils';
import {
  Button,
  Flex,
  Header,
  IconButton,
  Input,
  Loader,
  Text,
} from '@components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useUserQuery } from '@features/api/userSlice';
import {
  BackButton,
  BottomPanelButtonsContainer,
  CancelIcon,
  Container,
  StyledHeaderContainer,
  TableContainer,
} from './Agents.styles';
import { Edit } from '@mui/icons-material';
import { ActionsMenu } from './ActionsMenu/ActionsMenu';
import { EditCreateDialog } from './EditCreateDialog/EditCreateDialog';
import { useNomineeAgentsQuery } from '@features/api/nomineeAgentSlice';
import { useNomineeAgentsFilter } from './Agents.hooks';
import { useMediaQueries } from '@utils/mediaQueries/mediaQueries';

const rowsPerPage = 8;
const Agents: React.FC = () => {
  const theme = useTheme();
  const intl = useIntl();
  const { sm: isMobile } = useMediaQueries();

  const { data: user, isLoading: isLoadingUser } = useUserQuery();
  const { data: nomineeAgents, isLoading: isLoadingAgents } =
    useNomineeAgentsQuery();

  const {
    values,
    handlers: {
      handleNameFilterChange,
      handleEmailFilterChange,
      handleSearchButtonClick,
      handleClearButtonClick,
    },
  } = useNomineeAgentsFilter(nomineeAgents ?? []);

  const { filterCriteria, filteredData, isSearchButtonEnabled } = values;

  const [editingAgent, setEditingAgent] = useState<NomineeAgent | undefined>();

  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof NomineeAgent>('id');
  const [page, setPage] = useState(1);

  const handleEditAgent = (agent: NomineeAgent) => {
    setEditingAgent(agent);
    setIsSidePanelOpen(true);
  };

  const handleCloseSidePanel = () => {
    setIsSidePanelOpen(false);
    setEditingAgent(undefined);
  };

  const handleOpenSidePanel = () => {
    setIsSidePanelOpen(true);
  };

  const handleRequestSort = (_e: unknown, property: keyof NomineeAgent) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    setPage(newPage);
  };

  const renderFilters = () => (
    <>
      <Box flex={1}>
        <Input
          label={intl.formatMessage({ id: 'agents.name' })}
          onChange={handleNameFilterChange}
          value={filterCriteria?.name}
          fullWidth={isMobile}
        />
      </Box>
      <Box flex={1}>
        <Input
          label={intl.formatMessage({ id: 'agents.email' })}
          onChange={handleEmailFilterChange}
          value={filterCriteria?.email}
          fullWidth={isMobile}
        />
      </Box>
    </>
  );

  if (!nomineeAgents) return <></>;
  if (isLoadingUser || isLoadingAgents) return <Loader isLoading />;

  const totalPages = Math.ceil(nomineeAgents?.length / rowsPerPage);
  const filtersLength = Object.values(filterCriteria ?? {}).length;

  return (
    <Container>
      <StyledHeaderContainer>
        <Header
          user={user}
          title={{
            name: intl.formatMessage({ id: 'menu.agents' }),
            to: '/agents',
          }}
        />
      </StyledHeaderContainer>

      {!isMobile ? (
        <Flex justifyContent='space-between'>
          <Box>
            <Flex>
              <BackButton
                variant='tertiary'
                onClick={handleClearButtonClick}
                disabled={!filtersLength}
              >
                <FormattedMessage id='misc.clearAll' />
              </BackButton>
            </Flex>
            <Flex gap={6} alignItems='center' marginBottom={10}>
              {renderFilters()}
              <Flex>
                <Button
                  variant='primary'
                  sx={{ height: 50 }}
                  disabled={!filtersLength || !isSearchButtonEnabled}
                  onClick={handleSearchButtonClick}
                >
                  <FormattedMessage id='misc.search' />
                </Button>
              </Flex>
            </Flex>
          </Box>
          <Flex>
            <Box my={12} alignSelf='flex-end'>
              <Button onClick={handleOpenSidePanel}>
                <FormattedMessage id='misc.add' />
              </Button>
            </Box>
          </Flex>
        </Flex>
      ) : (
        <Flex width='100%' marginY={10} gap={6}>
          <Button
            variant='secondary'
            onClick={() => setIsBottomSheetOpen(true)}
            fullWidth
            sx={{ height: 45 }}
          >
            {`${intl.formatMessage({ id: 'portfolio.transactions.filter' })} ${
              filtersLength ? `(${filtersLength})` : ''
            }`}
          </Button>
          <Button fullWidth onClick={handleOpenSidePanel} sx={{ height: 45 }}>
            <FormattedMessage id='misc.add' />
          </Button>
        </Flex>
      )}

      <TableContainer>
        <Flex overflow='auto'>
          <Table aria-labelledby='tableTitle'>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              headerCells={headerCells}
            />
            <TableBody>
              {filteredData?.length ? (
                filteredData
                  ?.slice()
                  .sort((a, b) => {
                    const result = getComparator(order, orderBy)(a, b);
                    const equalValuesResult = 0;
                    // If the values are equal we additionally sort on the ID
                    if (result === equalValuesResult) {
                      const idComparisonResult = getComparator(order, 'id')(
                        a,
                        b
                      );
                      return idComparisonResult;
                    }

                    return result;
                  })
                  .slice(
                    (page - 1) * rowsPerPage,
                    (page - 1) * rowsPerPage + rowsPerPage
                  )
                  .map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${row.id}`;
                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={row.id}
                        sx={{
                          backgroundColor:
                            index % 2 === 0
                              ? theme.palette.grey[100]
                              : theme.palette.common.white,
                        }}
                      >
                        <TableCell
                          component='th'
                          id={labelId}
                          scope='row'
                          padding='normal'
                          align='right'
                        >
                          {row.id}
                        </TableCell>
                        <TableCell align='right'>{row.email}</TableCell>
                        <TableCell align='right'>{row.name}</TableCell>
                        <TableCell align='right' size='small'>
                          <IconButton
                            aria-label='edit'
                            color='success'
                            icon={Edit}
                            onClick={() => handleEditAgent(row)}
                          />
                          <ActionsMenu agentId={row.id} />
                        </TableCell>
                      </TableRow>
                    );
                  })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align='center'>
                    <Text
                      variant='h5'
                      fontWeight='fontWeightMedium'
                      sx={{ color: theme.palette.grey[700] }}
                    >
                      <FormattedMessage id='misc.noResults' />
                    </Text>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Flex>
        <Flex width='100%' justifyContent='center' padding={8}>
          <Pagination
            color='primary'
            shape='rounded'
            count={totalPages}
            page={page}
            onChange={handleChangePage}
          />
        </Flex>
      </TableContainer>

      <EditCreateDialog
        isOpen={isSidePanelOpen}
        onClose={handleCloseSidePanel}
        nomineeAgent={editingAgent}
      />

      {isMobile ? (
        <SwipeableDrawer
          anchor='bottom'
          open={isBottomSheetOpen}
          onClose={() => setIsBottomSheetOpen(false)}
          onOpen={() => setIsBottomSheetOpen(true)}
          keepMounted={false}
          PaperProps={{
            sx: {
              height: '90vh',
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
            },
          }}
        >
          <Box padding={10}>
            <Flex flex={1} alignItems='center' justifyContent='space-between'>
              <Text variant='h4' fontWeight='fontWeightMedium'>
                <FormattedMessage id='portfolio.transactions.selectFilters' />
              </Text>
              <IconButton
                data-testid='close-button'
                size='small'
                iconBoxHeight={25}
                iconBoxWidth={25}
                icon={() => <CancelIcon color='info' />}
                onClick={() => setIsBottomSheetOpen(false)}
              />
            </Flex>
            <Flex
              marginTop={18}
              gap={10}
              flexDirection='column'
              justifyContent='flex-start'
            >
              {renderFilters()}
            </Flex>
            <BottomPanelButtonsContainer>
              <Button
                variant='secondary'
                sx={{ flex: 1, height: 45 }}
                onClick={handleClearButtonClick}
              >
                <FormattedMessage id='misc.clear' />
              </Button>
              <Button
                sx={{ flex: 1, height: 45 }}
                disabled={!filtersLength || !isSearchButtonEnabled}
                onClick={() => {
                  handleSearchButtonClick();
                  setIsBottomSheetOpen(false);
                }}
              >
                {`${intl.formatMessage({
                  id: 'misc.apply',
                })} ${filtersLength ? `(${filtersLength})` : ''}`}
              </Button>
            </BottomPanelButtonsContainer>
          </Box>
        </SwipeableDrawer>
      ) : null}
    </Container>
  );
};

export default Agents;
